import { t } from 'i18next'
import React, { ReactNode } from 'react'
import { SVGIcon } from '@shared/Frontend/utils/svg'

// Unlike the shared `Paginator` (Main/Shared/Frontend/components/paginator.tsx), this
// component does not read or write the `page` URL search param. It only tracks page
// state locally, so pagination stays contained within the block that renders it.
export const BlockPaginator = ({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}) => {
  const pageNumbersBefore = []
  for (let page = Math.max(1, currentPage - 4); page < currentPage; page++)
    pageNumbersBefore.push(<BlockPaginatorNumber page={page} key={page} onPageChange={onPageChange} />)

  const pageNumbersAfter = []
  for (let page = currentPage + 1; page <= Math.min(totalPages, currentPage + 4); page++)
    pageNumbersAfter.push(<BlockPaginatorNumber page={page} key={page} onPageChange={onPageChange} />)

  return (
    <nav className="pager ls-spacing" aria-label={t('Pager:A11YLabel')}>
      <ul className="pager__list text--sm">
        <li className="pager__item pager__item--prev">
          <BlockPaginatorButton
            className="pager__prev"
            page={currentPage - 1}
            onPageChange={onPageChange}
            isDisabled={currentPage <= 1}
          >
            <SVGIcon id='icon-arrow-left' size={20} className='pager__icon' />
            <PreviousLabel />
          </BlockPaginatorButton>
        </li>

        <li className="pager__item pager__item--pages">
          <ol className="pager__pages">
            {currentPage > 5 && (
              <>
                <BlockPaginatorNumber page={1} onPageChange={onPageChange} />
                {currentPage > 6 && <Dots />}
              </>
            )}

            {pageNumbersBefore}

            <BlockPaginatorNumber page={currentPage} onPageChange={onPageChange} isCurrent />

            {pageNumbersAfter}

            {currentPage <= totalPages - 5 && (
              <>
                {currentPage <= totalPages - 6 && <Dots />}
                <BlockPaginatorNumber page={totalPages} onPageChange={onPageChange} />
              </>
            )}
          </ol>
        </li>

        <li className="pager__status text--sm" aria-hidden="true">
          <span className="pager__current">
            <em>{currentPage}</em>
            <span>/</span>
            {totalPages}
          </span>
        </li>

        <li className="pager__item pager__item--next">
          <BlockPaginatorButton
            className="pager__next"
            page={currentPage + 1}
            onPageChange={onPageChange}
            isDisabled={currentPage >= totalPages}
          >
            <NextLabel />
            <SVGIcon id='icon-arrow-right' size={20} className='pager__icon' />
          </BlockPaginatorButton>
        </li>
      </ul>
    </nav>
  )
}

const BlockPaginatorNumber = ({
  page,
  onPageChange,
  isCurrent,
}: {
  page: number
  onPageChange: (page: number) => void
  isCurrent?: boolean
}) => (
  <li className="pager__page">
    <BlockPaginatorButton
      className={`pager__action${isCurrent ? ' pager__action--active' : ''}`}
      page={page}
      onPageChange={onPageChange}
      isDisabled={false}
      isCurrent={isCurrent}
    >
      <PageNumberLabel page={page} />
    </BlockPaginatorButton>
  </li>
)

const BlockPaginatorButton = ({
  className,
  page,
  onPageChange,
  children,
  isDisabled,
  isCurrent,
}: {
  className: string
  page: number
  onPageChange: (page: number) => void
  children: ReactNode
  isDisabled: boolean
  isCurrent?: boolean
}) => {
  const current = `Page ${page} (current page)`
  const notCurrent = `Go to page ${page}`
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        if (isDisabled) return
        onPageChange(page)
      }}
      aria-current={isCurrent ? 'page' : undefined}
      aria-disabled={isDisabled || undefined}
      aria-label={isCurrent ? current : notCurrent}
      title={isCurrent ? current : notCurrent}
      tabIndex={isDisabled ? -1 : undefined}
    >
      {children}
    </button>
  )
}

const PageNumberLabel = ({ page }: { page: number }) => <span className="pager__number">{page}</span>

const Dots = () => (
  <li className="pager__page pager__page--dots" aria-hidden="true">
    <span className="pager__dots">...</span>
  </li>
)

const PreviousLabel = () => <span className='pager__label'>{t<string>('Pager:previous')}</span>
const NextLabel = () => <span className='pager__label'>{t<string>('Pager:next')}</span>
